// components/Assets/Instruments.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import useAxios from "../../utils/useAxios";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Grid,
  IconButton,
  List,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import { DesignServices, SwapVert } from "@mui/icons-material";
import { Button, Carousel, Divider, Dropdown, Image, Input } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { Layout } from "antd";
import {
  DownOutlined,
  LeftOutlined,
  MenuFoldOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useBreadcrumbs } from "../../context/BreadcrumbContext";
import NewInstrument from "../Forms/NewInstrument";
import GlobalSearch from "../Search/GlobalSearch";
import { useAccessControl } from "../../hooks/useAccessControl";
import InstrumentCard from "./InstrumentCard";
import EditInstrument from "../Forms/EditInstrument";
import InstrumentInfo from "../Forms/InstrumentInfo";
import PanelComponentCard from "./PanelComponentCard";
import ComponentInfo from "../Forms/ComponentInfo";
import EditComponent from "../Forms/EditComponent";
import { useBatchAccessControl } from "../../hooks/useAccessControlBatch";
import LogisticsProgressPanel from "../Logistics/LogisticsProgressPanel";
import FaultDashboard from "../Diagnostics/FaultDashboard";
import { FAULT_COLORS, getHighestSeverity } from "../../utils/faults";
import InstrumentCsvUpload from "../Forms/InstrumentCsvUpload";
import { queryAndMergeLogisticsProgress } from "../../utils/logisticsProgressLookup";
import { useLogisticsProgressUpdated } from "../../features/Logistics/context/logisticsUpdateLauncherContext";

const { Header, Content, Sider, Footer } = Layout;

// ── Grouping config ──────────────────────────────────────────────────────────
const GROUP_OPTIONS = [
  {
    key: "instrumentType",
    label: "Type",
    getKey: (i) => i.instrument_type_details?.name || "Unassigned",
  },
  {
    key: "ioPanel",
    label: "IO Panel",
    getKey: (i) =>
      i.panel_details?.tagNumber ||
      i.panel_details?.name ||
      i.panel_details?.description ||
      "Unassigned",
  },
  {
    key: "pid",
    label: "P&ID",
    getKey: (i) =>
      i.pid_details?.tagNumber || i.pid_details?.name || "Unassigned",
  },
  {
    key: "plc",
    label: "PLC",
    // ⚠️ Requires: plc_details = EquipmentSummarySerializer(source='plc', read_only=True)
    getKey: (i) =>
      i.plc_details?.tagNumber ||
      i.plc_details?.name ||
      (i.plc ? `PLC #${i.plc}` : "Unassigned"),
  },
];
// ────────────────────────────────────────────────────────────────────────────

const headerStyle = {
  textAlign: "center",
  color: "#000",
  backgroundColor: "#fff",
  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
};

const contentStyle = {
  textAlign: "center",
  minHeight: 120,
  lineHeight: "120px",
  color: "#000",
  overflow: "auto",
  backgroundColor: "#fff",
};

const siderStyle = {
  background: "#fff",
  padding: "16px",
  overflowY: "auto",
  borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
};

const footerStyle = {
  background: "#f5f5f5",
  padding: "24px",
  borderTop: "1px solid #ddd",
  minHeight: "100px",
};

const layoutStyle = {
  display: "flex",
  flexDirection: "column",
  maxWidth: "100vw !important",
  width: "100vw",
  overflow: "hidden",
  justifyContent: "start",
  height: "90vh",
  position: "relative",
};

const Instruments = () => {
  const api = useAxios();
  const { breadcrumbs, setComponent } = useBreadcrumbs();
  const toast = useToast();
  const navigate = useNavigate();
  const carouselRef = useRef(null);

  const { siteId, plantId, assetId, subAssetId, componentId } = useParams();

  const [viewMode, setViewMode] = useState("list");
  const [groupBy, setGroupBy] = useState(null);
  const { Search } = Input;

  // State management
  const [instruments, setInstruments] = useState([]);
  const [components, setComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [componentImageUrls, setComponentImageUrls] = useState([]);

  const [editInstrument, setEditInstrument] = useState(null);
  const [editComponent, setEditComponent] = useState(null);
  const [infoInstrument, setInfoInstrument] = useState(null);
  const [infoComponent, setInfoComponent] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [openEditInstrument, setOpenEditInstrument] = useState(false);
  const [openEditComponent, setOpenEditComponent] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isComponentInfoModalOpen, setIsComponentInfoModalOpen] =
    useState(false);

  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const [searchInstrumentsQuery, setSearchInstrumentsQuery] = useState("");
  const [searchComponentsQuery, setSearchComponentsQuery] = useState("");

  const [searchResults, setSearchResults] = useState(null);
  const isSearchActive = searchResults !== null;

  const { canEdit } = useAccessControl("panelcomponent", componentId);

  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);

  const baseUrl = `/sites/${siteId}/plants/${plantId}/assets/${assetId}/sub-asset/${subAssetId}/components/`;

  const handleLoadingChange = useCallback((isLoading) => {
    setGlobalSearchLoading(isLoading);
  }, []);

  // ── Fetch images for the selected panel component ─────────────────────────
  const fetchComponentImages = useCallback(
    async (component) => {
      if (!component?.id) {
        setComponentImageUrls([]);
        return;
      }
      try {
        const response = await api.get(
          `/component-images/?component=${component.id}`,
        );
        const results = Array.isArray(response.data)
          ? response.data
          : (response.data?.results ?? []);
        setComponentImageUrls(
          results
            .map((img) => img.image_url)
            .filter((url) => typeof url === "string" && url.trim() !== ""),
        );
      } catch {
        setComponentImageUrls([]);
      }
    },
    [api],
  );

  // Fetch components for the drawer
  const fetchComponents = useCallback(async () => {
    if (!subAssetId) return;
    try {
      const response = await api.get(`/panel-components/?tier=${subAssetId}`);
      const componentsData = Array.isArray(response.data)
        ? response.data
        : (response.data?.results ?? []);

      // Logistics progress enrichment start
      const componentsWithProgress = await queryAndMergeLogisticsProgress(
        componentsData,
        "panelcomponent",
      );
      // Logistics progress enrichment end

      setComponents(componentsWithProgress);

      const matchedComponent = componentsWithProgress.find(
        (c) => String(c.id) === String(componentId),
      );
      const resolvedComponent =
        matchedComponent || componentsWithProgress[0] || null;
      setSelectedComponent(resolvedComponent);
      if (resolvedComponent) {
        setComponent({
          name: resolvedComponent.tagNumber,
          id: resolvedComponent.id,
        });
      }
    } catch (error) {
      console.error("Error fetching components:", error);
      setComponents([]);
    }
  }, [api, subAssetId, componentId, setComponent]);

  // Fetch instruments for the selected component - both its structural
  // children (?component=) and any instruments cross-linked to it via the
  // "Related Assets" picker (?relatedComponents=), merged into one plain
  // list so a linked instrument renders the same as the rest, no separate
  // section or styling calling it out.
  const fetchInstruments = useCallback(async () => {
    if (!selectedComponent) {
      setInstruments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [structuralRes, relatedRes] = await Promise.all([
        api.get(`/instruments/?component=${selectedComponent.id}`),
        api.get(`/instruments/?relatedComponents=${selectedComponent.id}`),
      ]);
      const getResultsArray = (data) =>
        Array.isArray(data) ? data : (data?.results ?? []);
      const structural = getResultsArray(structuralRes.data);
      const related = getResultsArray(relatedRes.data);
      const byId = new Map(
        [...structural, ...related].map((item) => [item.id, item]),
      );
      const instrumentsData = Array.from(byId.values());

      // Logistics progress enrichment start
      const instrumentsWithProgress = await queryAndMergeLogisticsProgress(
        instrumentsData,
        "instrument",
      );
      // Logistics progress enrichment end

      setInstruments(instrumentsWithProgress);
    } catch (error) {
      console.error("Error fetching instruments:", error);
      setInstruments([]);
    } finally {
      setLoading(false);
    }
  }, [api, selectedComponent]);

  const newInstrumentItem = [
    {
      label: <NewInstrument reloadComponents={fetchInstruments} />,
      key: "new-instrument",
    },
    {
      label: (
        <InstrumentCsvUpload
          reloadInstruments={fetchInstruments}
          siteId={siteId}
          plantId={plantId}
          assetId={assetId}
          tierId={subAssetId}
          componentId={componentId}
        />
      ),
      key: "upload-instrument-excel",
    },
  ];

  useEffect(() => {
    fetchComponents();
  }, [fetchComponents]);

  // A logistics update from a card's menu changes the progress these lists show.
  useLogisticsProgressUpdated(() => {
    fetchComponents();
    if (selectedComponent) fetchInstruments();
  });

  useEffect(() => {
    if (selectedComponent) {
      fetchInstruments();
      fetchComponentImages(selectedComponent);
    }
  }, [fetchInstruments, fetchComponentImages, selectedComponent]);

  // Batch load instrument permissions
  const instrumentPermissionItems = useMemo(
    () =>
      instruments.map((instrument) => ({
        contentType: "instrument",
        objectId: instrument.id,
      })),
    [instruments],
  );

  const { isLoading: permissionsLoading } = useBatchAccessControl(
    instrumentPermissionItems,
  );

  // Drawer toggle
  const toggleDrawer = (inOpen) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpen(inOpen);
  };

  // Search handling
  const handleSearchResults = useCallback((items, active) => {
    setSearchResults(active ? items : null);
  }, []);

  // Component selection handler
  const handleComponentChange = useCallback(
    (component) => {
      setSelectedComponent(component);
      navigate(
        `/sites/${siteId}/plants/${plantId}/assets/${assetId}/sub-asset/${subAssetId}/components/${component.id}`,
      );
    },
    [navigate, siteId, plantId, assetId, subAssetId],
  );

  // Edit handlers
  const handleEditInstrument = (instrumentId) => {
    const instrument = instruments.find((i) => i.id === instrumentId);
    if (!instrument) return;
    setEditInstrument(instrument);
    setOpenEditInstrument(true);
  };

  const handleEditInstrumentClose = () => {
    setOpenEditInstrument(false);
    setEditInstrument(null);
  };

  const handleEditComponent = (compId) => {
    const component = components.find((c) => c.id === compId);
    if (!component) return;
    setEditComponent(component);
    setOpenEditComponent(true);
  };

  const handleEditComponentClose = () => {
    setOpenEditComponent(false);
    setEditComponent(null);
  };

  // Info handlers
  const handleInstrumentInfoClick = (instrument) => {
    setInfoInstrument(instrument);
    setIsInfoModalOpen(true);
  };

  const handleCloseInstrumentInfo = () => {
    setIsInfoModalOpen(false);
    setInfoInstrument(null);
  };

  const handleComponentInfoClick = (component) => {
    setInfoComponent(component);
    setIsComponentInfoModalOpen(true);
  };

  const handleCloseComponentInfo = () => {
    setIsComponentInfoModalOpen(false);
    setInfoComponent(null);
  };

  // Delete handlers
  const [deleteTarget, setDeleteTarget] = useState({ id: null, type: null });

  const handleDelete = async (id, type) => {
    try {
      const endpoint =
        type === "instrument"
          ? `/instruments/${id}/`
          : `/panel-components/${id}/`;

      await api.delete(endpoint);

      if (type === "instrument") {
        setInstruments((prev) => prev.filter((i) => i.id !== id));
      } else if (type === "component") {
        setComponents((prev) => prev.filter((c) => c.id !== id));
      }

      toast({
        title: "Success",
        description: `${
          type === "instrument" ? "Instrument" : "Component"
        } deleted successfully!`,
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });

      setOpenDialog(false);
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to delete ${
          type === "instrument" ? "instrument" : "component"
        }.`,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const handleDeleteClick = (id, type) => {
    setDeleteTarget({ id, type });
    setOpenDialog(true);
  };

  const handleCancelDelete = () => {
    setOpenDialog(false);
  };

  // Filtered data
  const filteredInstruments = useMemo(() => {
    const query = searchInstrumentsQuery.toLowerCase();
    return instruments.filter(
      (instrument) =>
        instrument.tagNumber?.toLowerCase().includes(query) ||
        instrument.description?.toLowerCase().includes(query),
    );
  }, [instruments, searchInstrumentsQuery]);

  const filteredComponents = useMemo(() => {
    const query = searchComponentsQuery.toLowerCase();
    return components.filter(
      (component) =>
        component.tagNumber?.toLowerCase().includes(query) ||
        component.componentName?.toLowerCase().includes(query) ||
        component.description?.toLowerCase().includes(query),
    );
  }, [components, searchComponentsQuery]);

  const cardsToShow = isSearchActive ? searchResults : filteredInstruments;

  // ── Grouped data ──────────────────────────────────────────────────────────
  // Returns ordered { label, items }[] — "Unassigned" always last
  const groupedCards = useMemo(() => {
    if (!groupBy) return null;
    const option = GROUP_OPTIONS.find((o) => o.key === groupBy);
    if (!option) return null;

    const map = {};
    for (const instrument of cardsToShow) {
      const key = option.getKey(instrument);
      if (!map[key]) map[key] = [];
      map[key].push(instrument);
    }

    return Object.entries(map)
      .sort(([a], [b]) => {
        if (a === "Unassigned") return 1;
        if (b === "Unassigned") return -1;
        return a.localeCompare(b);
      })
      .map(([label, items]) => ({ label, items }));
  }, [cardsToShow, groupBy]);
  // ────────────────────────────────────────────────────────────────────────

  // Logistics sidebar item
  const sidebarLogisticsItem = useMemo(
    () => selectedInstrument || selectedComponent || cardsToShow[0] || null,
    [selectedInstrument, selectedComponent, cardsToShow],
  );

  const safeImageUrls = componentImageUrls.filter(
    (url) => typeof url === "string" && url.trim() !== "",
  );

  // Shared card renderer
  const renderCards = (items) =>
    items.map((item) => (
      <InstrumentCard
        key={item.id}
        instrument={item}
        instrumentId={item.id}
        setSelectedInstrument={setSelectedInstrument}
        selectedInstrument={selectedInstrument}
        handleInfoClick={handleInstrumentInfoClick}
        handleDeleteClick={handleDeleteClick}
        handleEditInstrument={handleEditInstrument}
        viewMode={viewMode}
        permissionsLoading={permissionsLoading}
      />
    ));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        position: "relative",
        mt: { xs: "calc(56px)", sm: "calc(46px)" },
      }}
    >
      <Paper
        sx={{
          backgroundColor: "#f5f5f5",
          display: "flex",
          flexDirection: "column",
          padding: 1,
          alignItems: "start",
        }}
      >
        <Tooltip title="Components" placement="right">
          <IconButton
            onClick={toggleDrawer(!open)}
            size="medium"
            aria-label="components"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "primary.main",
            }}
          >
            <MenuFoldOutlined style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Paper>

      <Layout style={layoutStyle}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Header style={headerStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              width: "100%",
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "left",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  textAlign: "left",
                  fontWeight: 600,
                  mb: 0.5,
                  color: "primary.main",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  padding: 1,
                }}
              >
                {breadcrumbs.site?.name || "Please select a site"}
              </Typography>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 400,
                minWidth: 250,
                mx: "auto",
                display: "flex",
                alignItems: "center",
              }}
            >
              <GlobalSearch
                localItems={filteredInstruments}
                localSearchKeys={["tagNumber", "description"]}
                onResultsChange={handleSearchResults}
                onLoadingChange={handleLoadingChange}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {canEdit && (
                <Dropdown
                  menu={{ items: newInstrumentItem }}
                  placement="bottomLeft"
                  trigger={["click"]}
                >
                  <Button>
                    New <DownOutlined style={{ marginLeft: 8 }} />
                  </Button>
                </Dropdown>
              )}

              <IconButton>
                <SwapVert />
              </IconButton>

              {/* ── Group-by controls ──────────────────────────────────── */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: groupBy ? "primary.main" : "divider",
                  px: 0.75,
                  py: 0.25,
                  bgcolor: groupBy
                    ? "rgba(0,141,138,0.06)"
                    : "background.paper",
                  transition: "all 0.2s ease",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    pr: 0.5,
                    userSelect: "none",
                  }}
                >
                  Group
                </Typography>

                {GROUP_OPTIONS.map((option) => (
                  <Tooltip key={option.key} title={`Group by ${option.label}`}>
                    <Box
                      onClick={() =>
                        setGroupBy((prev) =>
                          prev === option.key ? null : option.key,
                        )
                      }
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: "5px",
                        cursor: "pointer",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        userSelect: "none",
                        transition: "all 0.15s ease",
                        color:
                          groupBy === option.key ? "#fff" : "text.secondary",
                        bgcolor:
                          groupBy === option.key
                            ? "primary.main"
                            : "transparent",
                        "&:hover": {
                          bgcolor:
                            groupBy === option.key
                              ? "primary.dark"
                              : "action.hover",
                        },
                      }}
                    >
                      {option.label}
                    </Box>
                  </Tooltip>
                ))}
              </Box>

              {/* View mode toggles */}
              <IconButton
                color={viewMode === "grid" ? "primary" : "default"}
                onClick={() => setViewMode("grid")}
                aria-label="Grid View"
              >
                <ViewModuleIcon />
              </IconButton>
              <IconButton
                color={viewMode === "list" ? "primary" : "default"}
                onClick={() => setViewMode("list")}
                aria-label="List View"
              >
                <ViewListIcon />
              </IconButton>
            </Box>
          </Box>
          <Divider sx={{ width: "100%" }} />
        </Header>

        {/* ── Body: Content + Sider ──────────────────────────────────────── */}
        <Layout>
          <Layout>
            <Content style={contentStyle}>
              <Grid
                container
                spacing={1}
                justifyContent="space-between"
                sx={{ width: "100%", overflow: "auto", padding: 2 }}
              >
                <Grid item xs={12}>
                  {loading || globalSearchLoading ? (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 2,
                        backgroundColor: "rgba(255,255,255,0.8)",
                        zIndex: 10,
                      }}
                    >
                      <CircularProgress size={48} />
                      {globalSearchLoading && (
                        <Typography variant="body2" color="text.secondary">
                          Searching across all records…
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      {/* Search active indicator */}
                      {isSearchActive && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: "primary.main",
                              animation: "pulse 1.5s ease-in-out infinite",
                              "@keyframes pulse": {
                                "0%, 100%": {
                                  opacity: 1,
                                  transform: "scale(1)",
                                },
                                "50%": {
                                  opacity: 0.5,
                                  transform: "scale(0.85)",
                                },
                              },
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "primary.main",
                              letterSpacing: 0.5,
                              textTransform: "uppercase",
                            }}
                          >
                            Search results
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, color: "text.disabled" }}
                          >
                            ({cardsToShow.length})
                          </Typography>
                        </Box>
                      )}

                      {/* ── Grouped layout (accordion) ─────────────────── */}
                      {groupedCards ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            width: "100%",
                          }}
                        >
                          {groupedCards.length === 0 ? (
                            <Box
                              sx={{
                                height: "40vh",
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="h6" color="text.secondary">
                                No instruments available for this component.
                              </Typography>
                            </Box>
                          ) : (
                            <Grid container spacing={2} sx={{ width: "100%" }}>
                              {groupedCards.map(({ label, items }) => {
                                // Derive header colour from the worst fault in the group,
                                // exactly as Equipment.jsx does for tiers.
                                const groupSeverity = getHighestSeverity(items);
                                const groupColor = FAULT_COLORS[groupSeverity];

                                return (
                                  <Grid item xs={12} key={label}>
                                    <Accordion
                                      defaultExpanded
                                      disableGutters
                                      elevation={0}
                                      sx={{
                                        borderRadius: "14px !important",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        overflow: "hidden",
                                        boxShadow:
                                          "0 4px 16px rgba(0,0,0,0.06)",
                                        "&:before": { display: "none" },
                                        "&.Mui-expanded": {
                                          boxShadow:
                                            "0 6px 24px rgba(0,0,0,0.09)",
                                        },
                                      }}
                                    >
                                      <AccordionSummary
                                        expandIcon={
                                          <ExpandMoreIcon
                                            sx={{ color: "#fff", fontSize: 22 }}
                                          />
                                        }
                                        aria-controls={`${label}-content`}
                                        id={`${label}-header`}
                                        sx={{
                                          bgcolor: groupColor,
                                          minHeight: "40px !important",
                                          px: 3,
                                          "&.Mui-expanded": {
                                            minHeight: "40px !important",
                                          },
                                          "& .MuiAccordionSummary-content": {
                                            my: "10px",
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle1"
                                            sx={{
                                              fontWeight: 700,
                                              color: "#fff",
                                              letterSpacing: 0.3,
                                            }}
                                          >
                                            {label}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "rgba(255,255,255,0.85)",
                                              fontWeight: 700,
                                            }}
                                          >
                                            {items.length}
                                          </Typography>
                                        </Box>
                                      </AccordionSummary>

                                      <AccordionDetails
                                        sx={{
                                          p: 2.5,
                                          bgcolor: "background.paper",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 2,
                                          }}
                                        >
                                          {renderCards(items)}
                                        </Box>
                                      </AccordionDetails>
                                    </Accordion>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                        </Box>
                      ) : (
                        /* ── Flat layout (no grouping) ──────────────────── */
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            justifyContent: "flex-start",
                            ...(isSearchActive && {
                              border: "2px solid",
                              borderColor: "primary.main",
                              borderRadius: "12px",
                              p: 2,
                              backgroundColor: "rgba(0, 141, 138, 0.02)",
                              transition: "all 0.2s ease",
                            }),
                          }}
                        >
                          {cardsToShow.length > 0 ? (
                            renderCards(cardsToShow)
                          ) : isSearchActive ? (
                            <Box
                              sx={{
                                width: "100%",
                                py: 6,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="h6" color="text.secondary">
                                No results found
                              </Typography>
                              <Typography variant="body2" color="text.disabled">
                                Try a different search term
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                height: "40vh",
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="h6" color="text.secondary">
                                No instruments available for this component.
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>

              {/* Fault dashboard below the card list */}
              <FaultDashboard
                items={filteredInstruments}
                itemLabel="instrument"
              />
            </Content>
          </Layout>

          {/* ── Sider ────────────────────────────────────────────────────── */}
          <Sider width="18%" style={siderStyle}>
            {selectedComponent && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    mb: 1.5,
                    borderBottom: "1px solid #eee",
                    pb: 0.5,
                  }}
                >
                  {selectedComponent.tagNumber ||
                    selectedComponent.componentName ||
                    "Component"}{" "}
                  Images
                </Typography>

                <Box sx={{ position: "relative" }}>
                  {safeImageUrls.length > 0 ? (
                    <>
                      <Image.PreviewGroup>
                        <Carousel
                          ref={carouselRef}
                          autoplay
                          autoplaySpeed={4000}
                          dots={false}
                          draggable
                          pauseOnHover
                          effect="scrollx"
                        >
                          {safeImageUrls.map((url, index) => (
                            <div key={index}>
                              <Image
                                src={url}
                                fallback="..."
                                style={{
                                  borderRadius: 12,
                                  objectFit: "cover",
                                  width: "100%",
                                  height: 220,
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  cursor: "pointer",
                                }}
                              />
                            </div>
                          ))}
                        </Carousel>
                      </Image.PreviewGroup>

                      {safeImageUrls.length > 1 && (
                        <>
                          <IconButton
                            onClick={() => carouselRef.current?.prev()}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: 0,
                              transform: "translateY(-50%)",
                              backgroundColor: "rgba(255,255,255,0.7)",
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,1)",
                              },
                            }}
                          >
                            <LeftOutlined />
                          </IconButton>
                          <IconButton
                            onClick={() => carouselRef.current?.next()}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              right: 0,
                              transform: "translateY(-50%)",
                              backgroundColor: "rgba(255,255,255,0.7)",
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,1)",
                              },
                            }}
                          >
                            <RightOutlined />
                          </IconButton>
                        </>
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "4 / 3",
                        border: "2px dashed #bbb",
                        borderRadius: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fafafa",
                        "&:hover": {
                          borderColor: "primary.main",
                          backgroundColor: "#f5f9ff",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontStyle: "italic" }}
                      >
                        No images available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {sidebarLogisticsItem && (
              <LogisticsProgressPanel
                item={sidebarLogisticsItem}
                insightItems={cardsToShow}
              />
            )}
          </Sider>
        </Layout>

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        <Footer style={footerStyle}>
          <Grid container spacing={2} justifyContent="center">
            {[
              {
                label: "Design & Engineering Docs",
                icon: (
                  <DesignServices sx={{ fontSize: 24, color: "#2e7d32" }} />
                ),
                disabled: !selectedComponent,
                onClick: () => {
                  if (!selectedComponent?.id) return;
                  navigate(`${baseUrl}${selectedComponent.id}/documents`);
                },
              },
              {
                label: "Faults / Diagnostics",
                icon: (
                  <WarningAmberRoundedIcon
                    sx={{ fontSize: 24, color: "#ff9966" }}
                  />
                ),
                disabled: !selectedInstrument,
                onClick: () => {
                  if (!selectedInstrument?.id) return;
                  navigate(
                    `${baseUrl}${componentId}/instruments/${selectedInstrument.id}/faults-diagnostics`,
                  );
                },
              },
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Box
                  component="div"
                  onClick={item.disabled ? undefined : item.onClick}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderRadius: "14px",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                    cursor: item.disabled ? "default" : "pointer",
                    opacity: item.disabled ? 0.45 : 1,
                    pointerEvents: item.disabled ? "none" : "auto",
                    transition: "all 0.25s ease",
                    "&:hover": item.disabled
                      ? {}
                      : {
                          boxShadow: "0 10px 32px rgba(0,0,0,0.12)",
                          transform: "translateY(-3px)",
                          borderColor: "primary.main",
                        },
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: "10px",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "grey.50",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      lineHeight: 1.3,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Footer>
      </Layout>

      {/* ── Components Drawer ────────────────────────────────────────────── */}
      <Drawer
        open={open}
        variant="persistent"
        onClose={toggleDrawer(false)}
        anchor="left"
        sx={{
          zIndex: 100,
          "& .MuiDrawer-paper": {
            width: 280,
            bgcolor: "background.paper",
            boxShadow: 2,
            p: 2,
            position: "absolute",
            top: "0px",
            left: "44px",
          },
        }}
      >
        <Box role="presentation" sx={{ width: "100%" }}>
          <Typography
            variant="h5"
            sx={{
              textAlign: "center",
              marginBottom: 2,
              fontWeight: "700",
              color: "primary.main",
              letterSpacing: "0.05em",
            }}
          >
            Components
          </Typography>
          <Divider />

          <Box sx={{ display: "flex", justifyContent: "center", margin: 2 }}>
            <Box sx={{ width: "100%", maxWidth: "100%" }}>
              <Search
                placeholder="Search components"
                allowClear
                enterButton
                value={searchComponentsQuery}
                onChange={(e) => setSearchComponentsQuery(e.target.value)}
                onSearch={(value) => setSearchComponentsQuery(value)}
              />
            </Box>
          </Box>

          <Divider />

          <List>
            {filteredComponents.map((item, index) => (
              <React.Fragment key={item.id}>
                <PanelComponentCard
                  component={item}
                  variant="minimal"
                  isSelected={selectedComponent?.id === item.id}
                  onSelect={handleComponentChange}
                  handleInfoClick={handleComponentInfoClick}
                  handleEditComponent={handleEditComponent}
                  handleDeleteClick={handleDeleteClick}
                />
                {index < filteredComponents.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <InstrumentInfo
        instrument={infoInstrument}
        open={isInfoModalOpen}
        onClose={handleCloseInstrumentInfo}
      />
      <EditInstrument
        instrument={editInstrument}
        open={openEditInstrument && !!editInstrument}
        onClose={handleEditInstrumentClose}
        onSuccess={fetchInstruments}
        siteId={siteId}
        plantId={plantId}
        // Read from the instrument's OWN current structural fields, not
        // this page's route params - since fetchInstruments now merges in
        // instruments only linked here via relatedComponents (not
        // structurally parented), using this page's componentId/etc.
        // would silently reassign a merely-related instrument's real
        // parent to this component on save (EditInstrument always
        // overwrites equipment/tier/component from these props).
        equipmentId={editInstrument?.equipment ?? null}
        tierId={editInstrument?.tier ?? null}
        componentId={editInstrument?.component ?? null}
      />
      <ComponentInfo
        component={infoComponent}
        open={isComponentInfoModalOpen}
        onClose={handleCloseComponentInfo}
      />
      <EditComponent
        component={editComponent}
        assets={[]}
        open={openEditComponent}
        onClose={handleEditComponentClose}
        onSuccess={fetchComponents}
      />

      {/* ── Delete dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={openDialog}
        onClose={handleCancelDelete}
        sx={{
          "& .MuiDialog-paper": {
            padding: 2,
            borderRadius: 3,
            boxShadow: 6,
            width: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: "bold", color: "error.main", textAlign: "center" }}
        >
          {deleteTarget.type === "instrument"
            ? "Delete Instrument"
            : "Delete Component"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontSize: "1rem",
              color: "text.secondary",
              textAlign: "center",
              mb: 2,
            }}
          >
            Are you sure you want to delete this{" "}
            {deleteTarget.type === "instrument" ? "instrument" : "component"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: "space-around", paddingBottom: 2 }}
        >
          <Button
            onClick={() => handleDelete(deleteTarget.id, deleteTarget.type)}
            variant="contained"
            color="error"
            sx={{ fontWeight: "bold" }}
          >
            Delete
          </Button>
          <Button
            onClick={handleCancelDelete}
            variant="contained"
            sx={{
              backgroundColor: "#007DAB",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#005f80" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Instruments;
